import {
  createFixtureLibraryAdapter,
  fixtureScenario,
} from "./library-fixture-adapter";
import type { LibraryAdapter } from "./library-adapter";
import {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode,
} from "react";
export type LibraryStatus =
  | "draft"
  | "ready"
  | "scheduled"
  | "published"
  | "archived";
export type LibraryExample =
  | "normal"
  | "empty"
  | "invalid"
  | "locked"
  | "files"
  | "versions"
  | "history"
  | "editor"
  | "upload"
  | "batch"
  | "send"
  | "duplicate"
  | "conflict"
  | "readonly";
export type LibraryFolder = {
  targetCustomerId?: string | null;
  targetInherited?: boolean;
  id: string;
  customerId: string;
  parentId: string | null;
  name: string;
};
export type LibraryRevision = {
  revision: number;
  body: string;
  files: string[];
  createdAt: string;
  valid: boolean;
};
export type LibraryUse = {
  jobId: string;
  executeCustomerId: string;
  runStatus: string;
  reviewStatus: string;
  postUrl: string | null;
  keywords: string[];
  revision: number;
  runAt: string;
};
export type LibraryItem = {
  ownerCustomerId?: string | null;
  targetCustomerId?: string | null;
  readOnly?: boolean;
  id: string;
  customerId: string;
  folderId: string | null;
  title: string;
  body: string;
  fileNames: string[];
  keywords: string[];
  memo: string;
  source: string;
  status: LibraryStatus;
  revision: number;
  valid: boolean;
  errors: number;
  warnings: number;
  locked: boolean;
  jobs: number;
  postedAt: string | null;
  postUrl: string | null;
  revisions: LibraryRevision[];
  uses: LibraryUse[];
};
export const libraryStatus: Record<LibraryStatus, string> = {
  draft: "작성 중",
  ready: "준비됨",
  scheduled: "발행 예정",
  published: "게시됨",
  archived: "보관",
};
const makeBody = (title: string) =>
  "---\n태그: 방문 안내\n공개: 전체\n---\n# " +
  title +
  "\n\n방문 전에 알아두면 좋은 내용을 정리했습니다. 운영 시간과 준비 사항을 확인해 주세요.\n\n## 방문 전 확인\n\n예약 시간과 안내 사항을 확인해 주세요.\n\n![안내 사진](guide.jpg)";
export function libraryFixtures(): LibraryItem[] {
  const titles = [
    "건강검진 전 준비 사항",
    "가을 진료 일정 안내",
    "사진 첨부를 확인할 원고",
    "10월 진료 안내",
    "독감 예방접종 안내",
    "예방접종 안내 복사본",
    "보관한 운영 안내",
    "게시 후 삭제된 안내",
  ];
  return titles
    .map((title, i) => {
      const body =
        i === 2
          ? "# 사진 첨부를 확인할 원고\n\n방문 준비 사항입니다.\n\n![사진](missing.jpg)"
          : makeBody(i === 5 ? titles[4] : title);
      const status: LibraryStatus = (
        [
          "ready",
          "ready",
          "draft",
          "scheduled",
          "published",
          "ready",
          "archived",
          "ready",
        ] as LibraryStatus[]
      )[i];
      const files = i === 2 ? [] : ["guide.jpg"],
        valid = i !== 2;
      const uses: LibraryUse[] =
        i >= 3 && i !== 5
          ? [
              {
                jobId: "JOB-" + (201 + i),
                executeCustomerId: i === 4 ? "moru" : "onyou",
                runStatus:
                  i === 3
                    ? "대기"
                    : i === 7
                      ? "완료"
                      : i === 6
                        ? "실패"
                        : "완료",
                reviewStatus:
                  i === 7 ? "게시 후 삭제" : i === 6 ? "실패" : "유지",
                postUrl: i === 4 ? "https://example.com/published" : null,
                keywords: ["강남 내과"],
                revision: 1,
                runAt: "2026-10-20 10:00",
              },
            ]
          : [];
      return {
        id: "ms-" + (i + 1),
        customerId: "onyou",
        folderId: i < 2 ? "folder-campaign" : i === 2 ? null : "folder-oct",
        title,
        body,
        fileNames: files,
        keywords: ["강남 내과", "건강검진"],
        memo: "",
        source: i === 6 ? "customer" : "editor",
        status,
        revision: 2,
        valid,
        errors: valid ? 0 : 1,
        warnings: 0,
        locked: status === "scheduled" || status === "published",
        jobs: uses.length,
        postedAt: i === 4 ? "2026-10-20 08:30" : null,
        postUrl: i === 4 ? "https://example.com/published" : null,
        revisions: [
          { revision: 2, body, files, createdAt: "10.20 09:00", valid },
          {
            revision: 1,
            body: body.replace("운영 시간과 준비 사항", "방문 시간"),
            files,
            createdAt: "10.19 16:20",
            valid,
          },
        ],
        uses,
      };
    })
    .concat([
      {
        id: "moru-ms",
        customerId: "moru",
        folderId: "folder-moru",
        title: "다른 고객사의 동일 내용 초안",
        body: makeBody("독감 예방접종 안내"),
        fileNames: ["guide.jpg"],
        keywords: ["성수 맛집"],
        memo: "",
        source: "upload",
        status: "ready",
        revision: 1,
        valid: true,
        errors: 0,
        warnings: 0,
        locked: false,
        jobs: 0,
        postedAt: null,
        postUrl: null,
        revisions: [],
        uses: [],
      },
    ]);
}
export const initialFolders: LibraryFolder[] = [
  {
    id: "folder-oct",
    customerId: "onyou",
    parentId: null,
    name: "2026년 10월",
  },
  {
    id: "folder-campaign",
    customerId: "onyou",
    parentId: "folder-oct",
    name: "건강검진 캠페인",
  },
  {
    id: "folder-empty",
    customerId: "onyou",
    parentId: null,
    name: "겨울 캠페인",
  },
  { id: "folder-moru", customerId: "moru", parentId: null, name: "가을 메뉴" },
];
export const bodyFingerprint = (body: string) => body.replace(/\s/g, "");
export function descendantIds(
  folders: { id: string; parentId: string | null }[],
  id: string,
): string[] {
  return [
    id,
    ...folders
      .filter((f) => f.parentId === id)
      .flatMap((f) => descendantIds(folders, f.id)),
  ];
}
type LibraryStore = {
  adapter: LibraryAdapter;
  items: LibraryItem[];
  folders: LibraryFolder[];
  setItems: React.Dispatch<React.SetStateAction<LibraryItem[]>>;
  setFolders: React.Dispatch<React.SetStateAction<LibraryFolder[]>>;
};
const LibraryContext = createContext<LibraryStore | null>(null);
export function ManuscriptLibraryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [storage] = useState(() => {
    let value = {
      items: libraryFixtures(),
      folders: structuredClone(initialFolders),
    };
    return {
      read: () => value,
      write: (next: typeof value) => {
        value = next;
      },
    };
  });
  const [items, setItemState] = useState(() => storage.read().items),
    [folders, setFolderState] = useState(() => storage.read().folders);
  const setItems: LibraryStore["setItems"] = (next) => {
    const value = storage.read();
    const items = typeof next === "function" ? next(value.items) : next;
    storage.write({ ...value, items });
    setItemState(items);
  };
  const setFolders: LibraryStore["setFolders"] = (next) => {
    const value = storage.read();
    const folders = typeof next === "function" ? next(value.folders) : next;
    storage.write({ ...value, folders });
    setFolderState(folders);
  };
  const [adapter] = useState(() =>
    createFixtureLibraryAdapter({
      read: storage.read,
      write: (next) => {
        storage.write(next);
        setItemState(next.items);
        setFolderState(next.folders);
      },
    }),
  );
  return (
    <LibraryContext.Provider
      value={{ items, folders, setItems, setFolders, adapter }}
    >
      {children}
    </LibraryContext.Provider>
  );
}
export function useManuscriptLibrary() {
  const v = useContext(LibraryContext);
  if (!v) throw new Error("ManuscriptLibraryProvider required");
  return v;
}

export function useLibraryAdapter(provider?: LibraryAdapter, example?: string) {
  const context = useContext(LibraryContext);
  const [fallback] = useState(() => createFixtureLibraryAdapter());
  const adapter = provider ?? context?.adapter ?? fallback;
  return useMemo(
    () => (provider ? provider : fixtureScenario(adapter, example)),
    [provider, adapter, example],
  );
}
